// COMP-RX-EDITOR — ویرایشگر نسخه در مواجهه (FLOW-13؛ US-MED-001، 003، 010 تا 016، 020، 021): جستجوی دارو (فارسی، انگلیسی، تجاری) با نشان
// «نمونه»، نسخه‌های آماده، اقلام با دوز، دفعات، مدت و تعداد محاسبه‌شده، قلم خارج از فهرست، بررسی ایمنی زنده با «منبع نمونه»، Override با دلیل
// (فقط med.safety.override)، داروی کنترل‌شده (تمدید زودهنگام و سقف با دلیل)، امضا با PIN (Ctrl+Enter)، چاپ A5 و لینک امن.
//
// AP-MED-03 (US-MED-074، 075): در بخش «ارسال» دو تیک «ارجاع» و «دستور بستری»، هم‌راستا با ثبت الکترونیک نسخه.
// امضا بی بازخورد ارجاع ورودی با PHX-MED-0303 رد می‌شود (BR-MED-072) و پیام آن از PinDialog دیده می‌شود.
//
// AP-MED-04 (US-MED-080، 081، 084، 085، 089): صافی دسته «مکمل» و «مکمل‌های من» در جستجو، تاریخ اجرای نسخه، پیشنهاد
// «معمولاً همراه این دارو» از آمار محلی، دکمه دیکته (تنها با موتور گفتار مستقر) و «ارسال برای تأیید استاد» که نسخه را
// PendingApproval می‌کند و ویرایش و امضای مستقیم را می‌بندد (PHX-MED-0327).
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type MedControlledFindingView,
  type MedDrugView,
  type MedOverrideReason,
  type MedPrescriptionItemRequest,
  type MedPrescriptionView,
  type MedSafetyAlert,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, FormDialog, SampleBadge, toJalali } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { PinDialog } from '../clinical/common';
import { COMP_RX_EDITOR, medKeys, num, RxStatusChip, SafetyAlerts, saveBlob } from './common';
import {
  EffectiveDateField,
  highVolumeKeys,
  TODAY_EFFECTIVE,
  toEffectiveRequest,
  useEffectiveDateOptions,
  type EffectiveDraft,
} from './highVolume';
import { NationalSubmissionPanel } from './national';
import { AdmissionOrderPanel, NationalReferralPanel } from './referralNational';
import { ReferentialServiceDialog } from './SupplementaryPanels';

export const FREQUENCIES = ['QD', 'QAM', 'QHS', 'BID', 'TID', 'QID', 'Q4H', 'Q6H', 'Q8H', 'Q12H', 'QOD', 'QW', 'PRN', 'ONCE'] as const;
const OVERRIDE_REASONS: readonly MedOverrideReason[] = ['BenefitOutweighsRisk', 'PreviouslyTolerated', 'DoseAdjusted', 'Other'];

/** قلم نسخه در ویرایشگر (نمایش + درخواست). */
export interface RxLine {
  request: MedPrescriptionItemRequest;
  label: string;
  isControlled: boolean;
  isSample: boolean;
}

export function lineFromDrug(drug: MedDrugView): RxLine {
  return {
    request: {
      kind: drug.kind,
      drugCode: drug.code,
      name: null,
      form: null,
      strength: null,
      doseValue: 1,
      doseUnit: drug.doseUnit,
      route: drug.route,
      frequency: 'QD',
      durationDays: 30,
      quantity: null,
      instructions: null,
    },
    label: [drug.genericName, drug.strength, drug.form].filter(Boolean).join(' '),
    isControlled: drug.isControlled,
    isSample: drug.isSample,
  };
}

function linesFromView(view: MedPrescriptionView): RxLine[] {
  return view.items.map((item) => ({
    request: {
      kind: item.kind,
      drugCode: item.drugCode,
      name: item.source === 'OffList' || item.kind === 'CareInstruction' ? item.name : null,
      form: item.source === 'OffList' ? item.form : null,
      strength: item.source === 'OffList' ? item.strength : null,
      doseValue: num(item.doseValue),
      doseUnit: item.doseUnit,
      route: item.route,
      frequency: item.frequency,
      durationDays: num(item.durationDays),
      quantity: item.quantityAdjusted ? num(item.quantity) : null,
      instructions: item.instructions,
    },
    label: item.displayName,
    isControlled: item.isControlled,
    isSample: item.isSample,
  }));
}

/** دیالوگ Override هشدار با دلیل فهرست + متن (BR-MED-021). */
function OverrideAlertDialog({ alert, prescriptionId, onClose, onDone }: { alert: MedSafetyAlert; prescriptionId: string; onClose: () => void; onDone: (view: MedPrescriptionView) => void }) {
  const { t } = useTranslation('med');
  const { tenantApi } = useTenantSession();
  const key = useIdempotencyKey();
  const [reason, setReason] = useState<MedOverrideReason>('BenefitOutweighsRisk');
  const [text, setText] = useState('');
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/prescriptions/{id}:override', {
          params: { path: { id: prescriptionId } },
          body: { alertKey: alert.key, reason, reasonText: text.trim() || null },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: onDone,
  });
  return (
    <FormDialog
      open
      titleKey="med:override.title"
      submitKey="med:override.submit"
      screenId="COMP-OVERRIDE"
      onSubmit={() => (reason === 'Other' && !text.trim() ? undefined : save.mutate())}
      onClose={onClose}
      isDirty={text.trim() !== ''}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      <Alert severity={alert.severity === 'Critical' ? 'error' : 'warning'}>{t(`med:alert_type.${alert.type}`)}</Alert>
      <TextField select label={t('med:override.reason')} value={reason} onChange={(event) => setReason(event.target.value as MedOverrideReason)} slotProps={{ htmlInput: { 'data-testid': 'med-override-reason' } }}>
        {OVERRIDE_REASONS.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`med:override_reason.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={t('med:override.text')}
        value={text}
        required={reason === 'Other'}
        multiline
        minRows={2}
        onChange={(event) => setText(event.target.value)}
        slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'med-override-text' } }}
      />
    </FormDialog>
  );
}

function ControlledFindings({ findings, earlyReason, limitReason, onEarly, onLimit }: { findings: readonly MedControlledFindingView[]; earlyReason: string; limitReason: string; onEarly: (value: string) => void; onLimit: (value: string) => void }) {
  const { t } = useTranslation('med');
  const early = findings.filter((finding) => finding.earlyRefill);
  const limit = findings.filter((finding) => finding.limitExceeded);
  if (early.length === 0 && limit.length === 0) {
    return null;
  }
  return (
    <Stack spacing={1} data-testid="med-controlled">
      {early.map((finding) => (
        <Alert key={`e-${finding.drugCode}`} severity="warning" data-testid="med-controlled-early">
          {t('med:controlled.early_refill', { name: finding.name, days: Math.round(num(finding.elapsedDays) ?? 0), history: finding.history.length })}
        </Alert>
      ))}
      {early.length > 0 && (
        <TextField label={t('med:controlled.early_reason')} required value={earlyReason} onChange={(event) => onEarly(event.target.value)} slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'med-early-reason' } }} />
      )}
      {limit.map((finding) => (
        <Alert key={`l-${finding.drugCode}`} severity="warning" data-testid="med-controlled-limit">
          {t('med:controlled.limit', { name: finding.name, max: num(finding.maxDays) ?? 0 })}
        </Alert>
      ))}
      {limit.length > 0 && (
        <TextField label={t('med:controlled.limit_reason')} required value={limitReason} onChange={(event) => onLimit(event.target.value)} slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'med-limit-reason' } }} />
      )}
    </Stack>
  );
}

export interface RxEditorProps {
  open: boolean;
  encounterId: string;
  patientId: string;
  /** پیش‌نویس موجود برای ادامه. */
  prescriptionId?: string | null;
  onClose: () => void;
  onChanged?: () => void;
}

export function RxEditor({ open, encounterId, patientId, prescriptionId, onClose, onChanged }: RxEditorProps) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const full = access.accessMode === 'Full';
  const canSign = full && access.can('med.prescription.sign');
  const canOverride = full && access.can('med.safety.override');
  const canFavorite = full && access.can('med.favorites.manage_own');
  const [lines, setLines] = useState<RxLine[]>([]);
  const [view, setView] = useState<MedPrescriptionView | null>(null);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<'' | 'Supplement'>('');
  const [effective, setEffective] = useState<EffectiveDraft>(TODAY_EFFECTIVE);
  const [approver, setApprover] = useState('');
  const [offList, setOffList] = useState('');
  const [overriding, setOverriding] = useState<MedSafetyAlert | null>(null);
  const [signing, setSigning] = useState(false);
  // AC-MED-074.1 و AC-MED-075.1: تیک‌های بخش ارسال؛ بسته‌بودن پیش‌فرض، چون ارجاع و بستری استثناست نه قاعده.
  const [withReferral, setWithReferral] = useState(false);
  const [withAdmission, setWithAdmission] = useState(false);
  const [earlyReason, setEarlyReason] = useState('');
  const [limitReason, setLimitReason] = useState('');
  const [templateName, setTemplateName] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // AC-MED-014.1 (AP-CLN-04): نسخه تکراری — حداکثر ۶ تکرار در ۶ ماه؛ داروی کنترل‌شده هرگز.
  const [repeats, setRepeats] = useState(0);
  const [repeatInterval, setRepeatInterval] = useState(30);

  const existing = useQuery({
    queryKey: medKeys.prescription(tenantId, prescriptionId ?? ''),
    enabled: open && Boolean(prescriptionId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/prescriptions/{id}', { params: { path: { id: prescriptionId ?? '' } } })),
  });
  useEffect(() => {
    if (existing.data) {
      setView(existing.data);
      setLines(linesFromView(existing.data));
      setRepeats(Number(existing.data.repeats ?? 0));
      setRepeatInterval(Number(existing.data.repeatIntervalDays ?? 30));
    }
  }, [existing.data]);

  const drugs = useQuery({
    queryKey: medKeys.drugs(tenantId, `${kindFilter}:${search.trim()}`),
    enabled: open && search.trim().length >= 2,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/drugs', { params: { query: { q: search.trim(), limit: 10, kind: kindFilter || undefined } } })),
  });
  const templates = useQuery({ queryKey: medKeys.templates(tenantId), enabled: open, queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/rx-templates')) });
  const requests = useMemo(() => lines.map((line) => line.request), [lines]);
  const draft = view?.status === 'Draft' || view === null;
  const pendingApproval = view?.status === 'PendingApproval';
  const effectiveOptions = useEffectiveDateOptions(null, open);

  // AC-MED-080.1: «مکمل‌های من» — پرکاربردهای ۹۰ روز اخیر همان پزشک در دسته مکمل.
  const frequent = useQuery({
    queryKey: highVolumeKeys.frequent(tenantId, kindFilter || 'all'),
    enabled: open && kindFilter === 'Supplement',
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/drugs/frequent', { params: { query: { kind: 'Supplement', limit: 10 } } })),
  });

  // AC-MED-084.3: دکمه دیکته تنها با Feature روشن و موتور گفتار مستقر دیده می‌شود.
  const [referential, setReferential] = useState(false);

  const voice = useQuery({
    queryKey: highVolumeKeys.voice(tenantId),
    enabled: open,
    queryFn: async () => (await unwrap(tenantApi.GET('/api/v1/medical-orders/voice-rx/availability'))) as unknown as { available: boolean },
  });

  // AC-MED-085.1: پیشنهاد «معمولاً همراه این دارو» برای آخرین داروی افزوده‌شده.
  const lastDrugCode = lines.filter((line) => line.request.drugCode).at(-1)?.request.drugCode ?? '';
  const coPrescriptions = useQuery({
    queryKey: highVolumeKeys.coPrescriptions(tenantId, lastDrugCode),
    enabled: open && draft && lastDrugCode.length > 0,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/co-prescriptions', { params: { query: { drugCode: lastDrugCode } } })),
  });

  // AC-MED-089.1: استادهای واجد شرط همان شعبه (پیش‌فرض عضو Primary یا Supervisor تیم مواجهه).
  const approvers = useQuery({
    queryKey: highVolumeKeys.approvers(tenantId, encounterId),
    enabled: open && draft && access.can('med.prescription.request_approval'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/encounters/{encounterId}/approvers', { params: { path: { encounterId } } })),
  });
  const preview = useQuery({
    queryKey: [...medKeys.patient(tenantId, patientId, 'safety-preview'), JSON.stringify(requests)],
    enabled: open && draft && requests.some((item) => item.kind !== 'CareInstruction'),
    queryFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/safety:check', {
          body: { patientId, items: requests },
        }),
      ),
  });

  const persist = async (): Promise<MedPrescriptionView> => {
    const headers = { 'Idempotency-Key': key.current() };
    const result = view
      ? await unwrap(tenantApi.PUT('/api/v1/medical-orders/prescriptions/{id}', { params: { path: { id: view.id } }, body: { items: requests, notes: view.notes, repeats, repeatIntervalDays: repeats > 0 ? repeatInterval : null, effective: toEffectiveRequest(effective) } }))
      : await unwrap(tenantApi.POST('/api/v1/medical-orders/prescriptions', { body: { encounterId, items: requests, notes: null, repeats, repeatIntervalDays: repeats > 0 ? repeatInterval : null, copyFromId: null, prescriberProviderId: null, effective: toEffectiveRequest(effective) }, headers }));
    key.settle();
    setView(result);
    return result;
  };
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: medKeys.patient(tenantId, patientId, 'prescriptions') });
    onChanged?.();
  };

  const save = useMutation({ mutationFn: persist, onSuccess: () => { setNotice(t('med:rx.saved')); refresh(); } });
  const startOverride = useMutation({
    mutationFn: async (alert: MedSafetyAlert) => {
      await persist();
      return alert;
    },
    onSuccess: (alert) => setOverriding(alert),
  });
  const startSign = useMutation({ mutationFn: persist, onSuccess: () => setSigning(true) });

  // AC-MED-089.1: ارسال پیش‌نویس برای تأیید استاد؛ نسخه PendingApproval می‌شود و ویرایش و امضای مستقیم بسته می‌شود.
  const requestApproval = useMutation({
    mutationFn: async () => {
      const saved = await persist();
      await unwrap(
        tenantApi.POST('/api/v1/medical-orders/prescriptions/{id}:request-approval', {
          params: { path: { id: saved.id } },
          body: { approverProviderId: approver || null },
          headers: { 'Idempotency-Key': key.current() },
        }),
      );
      return saved.id;
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setNotice(t('med:approval.requested'));
      refresh();
      void queryClient.invalidateQueries({ queryKey: medKeys.prescription(tenantId, view?.id ?? '') });
    },
  });
  const sign = useMutation({
    mutationFn: (pin: string) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/prescriptions/{id}:sign', {
          params: { path: { id: view?.id ?? '' } },
          body: { pin, earlyRefillReason: earlyReason.trim() || null, limitReason: limitReason.trim() || null },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (signed) => {
      setSigning(false);
      setView(signed);
      setNotice(t('med:rx.issued', { number: signed.number ?? '' }));
      refresh();
    },
  });
  const share = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/medical-orders/prescriptions/{id}:share', { params: { path: { id: view?.id ?? '' } } })),
    onSuccess: () => setNotice(t('med:rx.shared')),
  });
  const saveTemplate = useMutation({
    mutationFn: (name: string) =>
      unwrap(tenantApi.POST('/api/v1/medical-orders/rx-templates', { body: { name, scope: 'Personal', items: requests }, headers: { 'Idempotency-Key': key.current() } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setTemplateName(null);
      void queryClient.invalidateQueries({ queryKey: medKeys.templates(tenantId) });
      setNotice(t('med:rx.template_saved'));
    },
  });
  const print = async () => {
    if (!view) {
      return;
    }
    const response = await tenantApi.GET('/api/v1/medical-orders/prescriptions/{id}/print', { params: { path: { id: view.id }, query: { format: 'Pdf' } }, parseAs: 'blob' });
    if (response.response.ok && response.data) {
      saveBlob(response.data as unknown as Blob, `${view.number ?? 'rx'}.pdf`);
    }
  };

  // Ctrl+Enter: امضا (FLOW-13).
  useEffect(() => {
    if (!open || !canSign) {
      return undefined;
    }
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && draft && lines.length > 0) {
        event.preventDefault();
        startSign.mutate();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, canSign, draft, lines.length, startSign]);

  const safety = view?.status === 'Draft' && view.safety && preview.data === undefined ? view.safety : preview.data?.safety;
  const overridden = new Set((view?.overrides ?? []).map((item) => item.alertKey));
  const shownSafety = safety ? { ...safety, alerts: safety.alerts.map((alert) => ({ ...alert, overridden: alert.overridden || overridden.has(alert.key) })) } : safety;
  const controlled = preview.data?.controlled ?? view?.controlled ?? [];
  const unresolved = (shownSafety?.alerts ?? []).some((alert) => alert.severity === 'Critical' && !alert.overridden);
  const needsEarly = controlled.some((finding) => finding.earlyRefill) && !earlyReason.trim();
  const needsLimit = controlled.some((finding) => finding.limitExceeded) && !limitReason.trim();
  const update = (index: number, patch: Partial<MedPrescriptionItemRequest>) =>
    setLines((current) => current.map((line, position) => (position === index ? { ...line, request: { ...line.request, ...patch } } : line)));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="med-rx-title" data-screen-id={COMP_RX_EDITOR}>
      <DialogTitle id="med-rx-title">
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{t('med:rx.title')}</span>
          {view && <RxStatusChip status={view.status} />}
          {view?.number && <bdi dir="ltr">{view.number}</bdi>}
          {view?.isControlled && <Chip size="small" color="secondary" label={t('med:rx.controlled')} data-testid="med-rx-controlled" />}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {notice && (
            <Alert severity="success" onClose={() => setNotice(null)} data-testid="med-rx-notice">
              {notice}
            </Alert>
          )}
          {view?.registrationExpired && <Alert severity="warning" data-testid="med-rx-registration-expired">{t('med:rx.registration_expired')}</Alert>}
          {draft && (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  fullWidth
                  label={t('med:rx.search')}
                  value={search}
                  autoFocus
                  onChange={(event) => setSearch(event.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'med-drug-search' } }}
                />
                <TextField
                  select
                  sx={{ minWidth: 220 }}
                  label={t('med:rx.templates')}
                  value=""
                  onChange={(event) => {
                    const template = templates.data?.items.find((item) => item.id === event.target.value);
                    if (template) {
                      setLines(template.items.map((item) => ({ request: item, label: item.name ?? item.drugCode ?? '', isControlled: false, isSample: false })));
                    }
                  }}
                  slotProps={{ htmlInput: { 'data-testid': 'med-rx-template' } }}
                >
                  {(templates.data?.items ?? []).map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name} · {t(`med:scope.${template.scope}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              {drugs.data && search.trim().length >= 2 && (
                <Paper variant="outlined">
                  <List dense data-testid="med-drug-results" aria-label={t('med:rx.search_results')}>
                    {drugs.data.items.length === 0 && (
                      <ListItemText sx={{ px: 2 }} primary={t('med:rx.no_results')} />
                    )}
                    {drugs.data.items.map((drug) => (
                      <ListItemButton
                        key={drug.code}
                        onClick={() => {
                          setLines((current) => [...current, lineFromDrug(drug)]);
                          setSearch('');
                        }}
                        data-testid="med-drug-option"
                      >
                        <ListItemText
                          primary={
                            <span>
                              {drug.genericName} {drug.strength} {drug.form}
                              {drug.brandNames.length ? ` (${drug.brandNames.join(t('med:common.list_separator'))})` : ''}{' '}
                              {drug.isControlled ? <Chip size="small" color="secondary" label={t('med:rx.controlled')} /> : null} {drug.isSample ? <SampleBadge /> : null}
                            </span>
                          }
                          secondary={[drug.inTenantList ? t('med:drug.in_list') : null, drug.availableInCenter ? t('med:drug.available') : null, drug.insuranceCoverage].filter(Boolean).join(' · ')}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              )}
              {/* AC-MED-080.1: صافی دسته مکمل و فهرست «مکمل‌های من» (پرکاربردهای ۹۰ روز اخیر همان پزشک). */}
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <TextField
                  select
                  size="small"
                  label={t('med:rx.kind_filter')}
                  value={kindFilter}
                  onChange={(event) => setKindFilter(event.target.value as '' | 'Supplement')}
                  slotProps={{ htmlInput: { 'data-testid': 'med-rx-kind-filter' } }}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">{t('med:rx.kind_all')}</MenuItem>
                  <MenuItem value="Supplement">{t('med:item_kind.Supplement')}</MenuItem>
                </TextField>
                {voice.data?.available ? (
                  <Button size="small" variant="outlined" data-testid="med-rx-voice" onClick={() => setNotice(t('med:rx.voice_hint'))}>
                    {t('med:rx.voice')}
                  </Button>
                ) : null}
                {/* AP-MED-05 (AC-MED-095.2): «انجام خدمت ارجاعی» پزشک دوم — جدا از «ارجاع پزشک» (نوع ۷). */}
                {access.can('med.prescription.national_submit') ? (
                  <Button size="small" variant="outlined" data-testid="med-rx-referential" onClick={() => setReferential(true)}>
                    {t('med:referential.open')}
                  </Button>
                ) : null}
              </Stack>
              {kindFilter === 'Supplement' && (frequent.data?.items ?? []).length > 0 ? (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" data-testid="med-rx-my-supplements">
                  {(frequent.data?.items ?? []).map((drug) => (
                    <Chip
                      key={drug.code}
                      size="small"
                      label={drug.genericName}
                      onClick={() => setLines((current) => [...current, lineFromDrug(drug)])}
                      data-testid="med-rx-my-supplement"
                    />
                  ))}
                </Stack>
              ) : null}
              {(coPrescriptions.data?.items ?? []).length > 0 ? (
                <Stack spacing={0.5} data-testid="med-rx-co-prescriptions">
                  <Typography variant="caption" color="text.secondary">
                    {t('med:rx.co_prescription')}
                  </Typography>
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {(coPrescriptions.data?.items ?? []).map((suggestion) => (
                      <Chip
                        key={suggestion.drugCode}
                        size="small"
                        variant="outlined"
                        label={suggestion.name ?? suggestion.drugCode}
                        onClick={() => setSearch(suggestion.name ?? suggestion.drugCode)}
                        data-testid="med-rx-co-suggestion"
                      />
                    ))}
                  </Stack>
                </Stack>
              ) : null}
              <Stack direction="row" spacing={1}>
                <TextField size="small" fullWidth label={t('med:rx.off_list')} value={offList} onChange={(event) => setOffList(event.target.value)} slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'med-offlist-name' } }} />
                <Button
                  onClick={() => {
                    if (!offList.trim()) {
                      return;
                    }
                    setLines((current) => [
                      ...current,
                      { request: { kind: 'Drug', drugCode: null, name: offList.trim(), form: null, strength: null, doseValue: 1, doseUnit: null, route: null, frequency: 'QD', durationDays: 30, quantity: null, instructions: null }, label: offList.trim(), isControlled: false, isSample: false },
                    ]);
                    setOffList('');
                  }}
                  data-testid="med-offlist-add"
                >
                  {t('med:rx.add')}
                </Button>
              </Stack>
            </>
          )}
          {lines.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('med:rx.empty')}
            </Typography>
          ) : (
            lines.map((line, index) => (
              <Paper key={`${line.label}-${index}`} variant="outlined" sx={{ p: 1 }} data-testid="med-rx-line">
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                    {line.label} {line.request.drugCode === null && line.request.kind === 'Drug' ? <Chip size="small" variant="outlined" label={t('med:rx.off_list_badge')} /> : null}{' '}
                    {line.isControlled ? <Chip size="small" color="secondary" label={t('med:rx.controlled')} /> : null} {line.isSample ? <SampleBadge /> : null}
                  </Typography>
                  {draft && (
                    <IconButton size="small" aria-label={t('med:rx.remove')} onClick={() => setLines((current) => current.filter((_, position) => position !== index))}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
                {line.request.kind !== 'CareInstruction' && (
                  <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' } }}>
                    <TextField size="small" type="number" label={t('med:rx.dose')} value={line.request.doseValue ?? ''} disabled={!draft} onChange={(event) => update(index, { doseValue: event.target.value === '' ? null : Number(event.target.value) })} slotProps={{ htmlInput: { min: 0, step: 0.25, 'data-testid': 'med-line-dose' } }} />
                    <TextField size="small" select label={t('med:rx.frequency')} value={line.request.frequency ?? ''} disabled={!draft} onChange={(event) => update(index, { frequency: event.target.value })} slotProps={{ htmlInput: { 'data-testid': 'med-line-frequency' } }}>
                      {FREQUENCIES.map((frequency) => (
                        <MenuItem key={frequency} value={frequency}>
                          {t(`med:frequency.${frequency}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField size="small" type="number" label={t('med:rx.days')} value={line.request.durationDays ?? ''} disabled={!draft} onChange={(event) => update(index, { durationDays: event.target.value === '' ? null : Number(event.target.value) })} slotProps={{ htmlInput: { min: 1, max: 365, 'data-testid': 'med-line-days' } }} />
                    <TextField size="small" type="number" label={t('med:rx.quantity')} value={line.request.quantity ?? ''} disabled={!draft} placeholder={t('med:rx.quantity_auto')} onChange={(event) => update(index, { quantity: event.target.value === '' ? null : Number(event.target.value) })} slotProps={{ htmlInput: { min: 0, 'data-testid': 'med-line-quantity' } }} />
                  </Box>
                )}
                <TextField sx={{ mt: 1 }} size="small" fullWidth label={t('med:rx.instructions')} value={line.request.instructions ?? ''} disabled={!draft} onChange={(event) => update(index, { instructions: event.target.value || null })} slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'med-line-instructions' } }} />
              </Paper>
            ))
          )}
          {draft && lines.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} data-testid="med-rx-repeat">
              <TextField
                size="small"
                type="number"
                label={t('med:rx.repeats')}
                value={repeats}
                disabled={lines.some((line) => line.isControlled)}
                helperText={lines.some((line) => line.isControlled) ? t('med:rx.repeats_controlled') : t('med:rx.repeats_hint')}
                onChange={(event) => setRepeats(Math.max(0, Math.min(6, Number(event.target.value) || 0)))}
                slotProps={{ htmlInput: { min: 0, max: 6, 'data-testid': 'med-rx-repeats' } }}
              />
              {repeats > 0 && (
                <TextField size="small" type="number" label={t('med:rx.repeat_interval')} value={repeatInterval} onChange={(event) => setRepeatInterval(Math.max(1, Number(event.target.value) || 1))} slotProps={{ htmlInput: { min: 1, max: 183, 'data-testid': 'med-rx-repeat-interval' } }} />
              )}
            </Stack>
          )}
          {view?.repeatSchedule && view.repeatSchedule.length > 0 && (
            <Alert severity="info" data-testid="med-rx-repeat-schedule">
              {t('med:rx.repeat_schedule', { count: view.repeatSchedule.length })}: {view.repeatSchedule.map((item) => toJalali(item.dueAt.slice(0, 10))).join(t('med:common.list_separator'))}
            </Alert>
          )}
          {/* AC-MED-081.1: تاریخ اجرای نسخه (امروز، بازه تقریبی یا تاریخ دقیق). */}
          {draft && <EffectiveDateField value={effective} options={effectiveOptions.data} onChange={setEffective} testId="med-rx-effective" />}
          {pendingApproval && (
            <Alert severity="info" data-testid="med-rx-pending-approval">
              {t('med:approval.pending_hint')}
            </Alert>
          )}
          {draft && <SafetyAlerts safety={shownSafety} canOverride={canOverride} onOverride={(alert) => startOverride.mutate(alert)} />}
          {draft && <ControlledFindings findings={controlled} earlyReason={earlyReason} limitReason={limitReason} onEarly={setEarlyReason} onLimit={setLimitReason} />}
          {draft && unresolved && !canOverride && <Alert severity="info">{t('med:safety.physician_only')}</Alert>}
          {(save.error || startSign.error || startOverride.error || share.error) && <ErrorState error={save.error ?? startSign.error ?? startOverride.error ?? share.error} compact />}
          {templateName !== null && (
            <Stack direction="row" spacing={1}>
              <TextField size="small" fullWidth label={t('med:rx.template_name')} value={templateName} onChange={(event) => setTemplateName(event.target.value)} slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'med-template-name' } }} />
              <Button onClick={() => templateName.trim() && saveTemplate.mutate(templateName.trim())} disabled={saveTemplate.isPending} data-testid="med-template-save">
                {t('med:rx.save_template')}
              </Button>
            </Stack>
          )}
          {/* AP-MED-02 (COMP-RX-EDITOR تب ارسال؛ US-MED-031): ثبت الکترونیک ملی نسخه صادرشده، کد رهگیری و صف خطا.
              ارسال ناهمگام است و امضا را مسدود نمی‌کند (NFR PRD-MED §۱۲). */}
          {view?.status === 'Issued' && (
            <NationalSubmissionPanel
              encounterId={encounterId}
              prescriptionId={view.id}
              canSubmit={access.can('med.prescription.national_submit')}
            />
          )}
          {/* AP-MED-03 (US-MED-074، 075): ارجاع افقی/عمودی و دستور بستری سرپایی کنار ارسال نسخه؛ هر دو
              ناهمگام‌اند و امضای نسخه را مسدود نمی‌کنند. */}
          {view?.status === 'Issued' && access.can('med.national_referral.submit') && (
            <Stack spacing={1} data-testid="med-rx-referral-section">
              <Divider />
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={withReferral}
                      onChange={(event) => setWithReferral(event.target.checked)}
                      data-testid="med-rx-with-referral"
                    />
                  }
                  label={t('med:rx.with_referral')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={withAdmission}
                      onChange={(event) => setWithAdmission(event.target.checked)}
                      data-testid="med-rx-with-admission"
                    />
                  }
                  label={t('med:rx.with_admission')}
                />
              </Stack>
              {withReferral && <NationalReferralPanel encounterId={encounterId} defaultKind="Vertical" />}
              {withAdmission && <AdmissionOrderPanel encounterId={encounterId} />}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <ReferentialServiceDialog open={referential} encounterId={encounterId} patientId={patientId} onClose={() => setReferential(false)} />
        <Button onClick={onClose}>{t('med:common.close')}</Button>
        {view?.status === 'Issued' && (
          <>
            <Button onClick={() => void print()} data-testid="med-rx-print">
              {t('med:rx.print')}
            </Button>
            {!view.isControlled && (
              <Button onClick={() => share.mutate()} disabled={share.isPending} data-testid="med-rx-share">
                {t('med:rx.share')}
              </Button>
            )}
          </>
        )}
        {draft && canFavorite && lines.length > 0 && templateName === null && (
          <Button onClick={() => setTemplateName('')} data-testid="med-template-start">
            {t('med:rx.save_template')}
          </Button>
        )}
        {draft && (
          <Button onClick={() => save.mutate()} disabled={lines.length === 0 || save.isPending} data-testid="med-rx-save">
            {t('med:rx.save_draft')}
          </Button>
        )}
        {/* AC-MED-089.1: ارسال پیش‌نویس برای تأیید استاد — تهیه‌کننده هرگز اعتبارنامه استاد را نمی‌بیند. */}
        {draft && lines.length > 0 && access.can('med.prescription.request_approval') && (approvers.data?.items ?? []).length > 0 && (
          <>
            <TextField
              select
              size="small"
              label={t('med:approval.approver')}
              value={approver || (approvers.data?.items ?? []).find((option) => option.isDefault)?.providerId || ''}
              onChange={(event) => setApprover(event.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'med-rx-approver' } }}
              sx={{ minWidth: 200 }}
            >
              {(approvers.data?.items ?? []).map((option) => (
                <MenuItem key={option.providerId} value={option.providerId}>
                  {option.displayName}
                  {option.isDefault ? ` · ${t('med:approval.default')}` : ''}
                </MenuItem>
              ))}
            </TextField>
            <Button onClick={() => requestApproval.mutate()} disabled={requestApproval.isPending} data-testid="med-rx-request-approval">
              {t('med:approval.request')}
            </Button>
          </>
        )}
        {draft && canSign && (
          <Button
            variant="contained"
            onClick={() => startSign.mutate()}
            disabled={lines.length === 0 || unresolved || needsEarly || needsLimit || startSign.isPending}
            aria-keyshortcuts="Control+Enter"
            data-testid="med-rx-sign"
          >
            {t('med:rx.sign')} (Ctrl+Enter)
          </Button>
        )}
      </DialogActions>
      {overriding && view && (
        <OverrideAlertDialog
          alert={overriding}
          prescriptionId={view.id}
          onClose={() => setOverriding(null)}
          onDone={(next) => {
            setOverriding(null);
            setView(next);
            void preview.refetch();
          }}
        />
      )}
      <PinDialog open={signing} titleKey="med:rx.sign_title" onClose={() => setSigning(false)} onConfirm={(pin) => sign.mutate(pin)} isSubmitting={sign.isPending} error={sign.error}>
        <Typography variant="body2">{t('med:rx.sign_message', { count: lines.length })}</Typography>
      </PinDialog>
    </Dialog>
  );
}
